/**
 * Workbench solver modal — Iter 290zp.15z03 (Layer C).
 *
 * Lets the rep type a plain-text build description and get a
 * structured BoM proposal back: matched real SKUs from inventory_attrs
 * + gap rows for anything the matcher couldn't find. The rep reviews
 * the result, then either copies the SKUs manually or hits "Apply" to
 * write them straight into the draft's BoM.
 */
import React, { useState } from "react";
import {
  Loader2, Wand2, X, AlertTriangle, CheckCircle2,
  Wrench, Pipette, Package,
} from "lucide-react";
import Modal from "../Modal";
import api from "../../lib/api";
import DictTextarea from "../DictTextarea";


export default function SolverModal({ open, onClose, draftId, onApplied }) {
  const [description, setDescription] = useState("");
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const reset = () => {
    setDescription(""); setRunning(false); setApplying(false);
    setErr(""); setResult(null);
  };
  const close = () => { reset(); onClose?.(); };

  const runSolver = async (applyImmediately = false) => {
    if (description.trim().length < 8) {
      setErr("Add a build description (at least 8 characters).");
      return;
    }
    setErr("");
    if (applyImmediately) setApplying(true); else setRunning(true);
    try {
      const { data } = await api.post(
        `/workbench/drafts/${draftId}/solver/generate`,
        { description: description.trim(), apply: applyImmediately },
        { timeout: 120000 },
      );
      setResult(data);
      if (applyImmediately) {
        await onApplied?.(data);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Solver failed.");
    } finally {
      setRunning(false); setApplying(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={
        <div className="flex items-center gap-2">
          <Wand2 className="w-4 h-4 text-cyan-300" />
          <span>Generate BoM from build description</span>
        </div>
      }
      testId="workbench-solver-modal"
      maxWidth="max-w-3xl"
      footer={
        <div className="flex items-center justify-between gap-3 w-full flex-wrap">
          <div className="text-[11px] text-[#647087]">
            {result
              ? `${result.matched_count} matched · ${result.gap_count} gap${result.gap_count === 1 ? "" : "s"}`
              : "Describe the pipework + fittings in plain English. The parser handles size + material + end-types automatically."}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={close}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="workbench-solver-cancel"
            >Close</button>
            {result ? (
              <button
                type="button"
                onClick={() => runSolver(true)}
                disabled={applying || (result.matched_count + result.gap_count) === 0}
                className="hv-btn hv-btn-primary text-xs"
                data-testid="workbench-solver-apply"
              >
                {applying ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                {applying ? "Adding to BoM…" : `Apply all ${result.matched_count + result.gap_count} rows`}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => runSolver(false)}
                disabled={running || description.trim().length < 8}
                className="hv-btn hv-btn-primary text-xs"
                data-testid="workbench-solver-run"
              >
                {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                {running ? "Solving…" : "Solve"}
              </button>
            )}
          </div>
        </div>
      }
    >
      {!result ? (
        <div className="space-y-2">
          <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">
            Build description
          </label>
          <DictTextarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='e.g. "20 m of 32mm PVC pressure pipe from the pump to the storage tank. 90° elbow at the wall, then a tee branching into two 25mm BSP lines. End with a 32mm ball valve before the tank inlet."'
            className="w-full min-h-[140px] bg-[#0a0b0e] border border-white/10 rounded p-2 text-sm font-mono text-white"
            data-testid="workbench-solver-input"
          />
          <p className="text-[10px] text-[#647087] italic">
            Tips: include lengths in metres, sizes in mm (inches OK — converted automatically), material (PVC / HDPE / brass), and end-types (BSP / glue / compression) where it matters.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Result counts banner */}
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded border border-emerald-400/30 bg-emerald-500/10 px-3 py-2">
              <div className="text-[10px] uppercase text-emerald-300/70 font-bold">Matched SKUs</div>
              <div className="text-xl font-semibold text-emerald-200">{result.matched_count}</div>
            </div>
            <div className="rounded border border-amber-400/30 bg-amber-500/10 px-3 py-2">
              <div className="text-[10px] uppercase text-amber-300/70 font-bold">Gaps to source</div>
              <div className="text-xl font-semibold text-amber-200">{result.gap_count}</div>
            </div>
            <div className="rounded border border-cyan-400/30 bg-cyan-500/10 px-3 py-2">
              <div className="text-[10px] uppercase text-cyan-300/70 font-bold">Components parsed</div>
              <div className="text-xl font-semibold text-cyan-200">
                {(result.parsed?.components || []).length + (result.parsed?.segments || []).length}
              </div>
            </div>
          </div>

          {/* Matched rows */}
          {result.matched_count > 0 && (
            <section data-testid="workbench-solver-matched">
              <div className="text-[10px] uppercase tracking-wider text-[#647087] font-bold mb-1.5 flex items-center gap-1">
                <Package className="w-3 h-3" /> Matched real SKUs
              </div>
              <div className="rounded border border-white/10 divide-y divide-white/5 max-h-[300px] overflow-y-auto">
                {(result.matched || []).map((m, i) => (
                  <div
                    key={`m-${i}`}
                    className="px-2 py-1.5 text-[11px] flex items-center gap-2"
                    data-testid={`workbench-solver-matched-${i}`}
                  >
                    <span className="font-mono text-cyan-300 text-[10px] shrink-0 w-24 truncate">{m.sku || "—"}</span>
                    <span className="flex-1 truncate text-white" title={m.name}>{m.name || "(no name)"}</span>
                    <span className="text-[10px] text-[#94a3b8] tabular-nums">
                      {m.qty}{m.unit ? ` ${m.unit}` : ""}
                    </span>
                    {m.sell_price_incl > 0 && (
                      <span className="text-[10px] text-[#94a3b8] tabular-nums w-16 text-right">
                        R{m.sell_price_incl.toFixed(2)}
                      </span>
                    )}
                    {m.approved && (
                      <span className="text-[9px] text-emerald-300 bg-emerald-500/10 border border-emerald-400/30 px-1 rounded">A</span>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Gap rows */}
          {result.gap_count > 0 && (
            <section data-testid="workbench-solver-gaps">
              <div className="text-[10px] uppercase tracking-wider text-amber-300/80 font-bold mb-1.5 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Gaps — not in inventory, source from supplier
              </div>
              <ul className="space-y-1">
                {(result.gaps || []).map((g, i) => (
                  <li
                    key={`g-${i}`}
                    className="text-[11px] text-amber-100/90 bg-amber-500/5 border border-amber-400/20 rounded px-2 py-1 flex items-start gap-1.5"
                    data-testid={`workbench-solver-gap-${i}`}
                  >
                    <Wrench className="w-3 h-3 mt-0.5 shrink-0 text-amber-300" />
                    <span className="flex-1 break-words">{g.description}</span>
                    <span className="text-[10px] text-amber-300/70 tabular-nums shrink-0">
                      {g.qty}{(g.kind || g.family) === "pipe" ? " m" : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {result.applied && (
            <div
              className="rounded border border-emerald-400/40 bg-emerald-500/10 p-2 text-xs text-emerald-200 flex items-center gap-2"
              data-testid="workbench-solver-applied"
            >
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              All {result.matched_count + result.gap_count} rows written to the BoM. Close to review.
            </div>
          )}

          <button
            type="button"
            onClick={() => { setResult(null); }}
            className="text-[10px] text-[#647087] hover:underline"
            data-testid="workbench-solver-restart"
          >
            ← Try a different description
          </button>
        </div>
      )}
      {err && (
        <div className="rounded border border-rose-400/40 bg-rose-500/10 p-2 text-xs text-rose-200 flex items-start gap-2 mt-2">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {err}
        </div>
      )}
    </Modal>
  );
}
